import {
  type ClassificationLevel,
  CSRF_HEADER,
  EGRESS_ACTIONS,
  EGRESS_AUDIENCES,
  EGRESS_RANK,
  type EgressAction,
  egressPolicySchema,
  isModelKey,
  MAX_WEB_SEARCH_DOMAINS,
  MODEL_CATALOGUE,
  type ProjectDetail,
  type ProjectSettingResponse,
  projectSettingLoosens,
  type SettingInput,
  settingLoosens,
  type WikiTrustLevel,
  webDomainsNarrow,
} from "@esi/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { McpGroupTools } from "../../admin/McpGroupTools.tsx";
import { api } from "../../api.ts";
import { apiError, ok } from "../../api-errors.ts";
import { t as translate } from "../../i18n/i18n.ts";
import { Link, MODELS_PATH } from "../../router.tsx";
import { useSession } from "../../session.tsx";
import {
  describeEgress,
  type EgressAudience,
  egressLoosenSentence,
} from "../../settings-shell/egress.ts";
import { SettingsSectionBlock } from "../../settings-shell/SettingsShell.tsx";
import { TypedSettingRow } from "../../settings-shell/TypedSettingRow.tsx";
import { projectKeys, useProjectSettings } from "../queries.ts";
import {
  BEHAVIOUR,
  type BehaviourDef,
  GROUPS,
  type Guidelines,
  MAX_AGE_DAYS,
  MAX_OCR_PAGES,
  maxAgeCeiling,
  PAGE_TYPES,
  pagesCeiling,
  WEB_FETCH_APPROVALS,
  WIKI_TRUST_LEVELS,
} from "./behaviour.ts";
import { describePlacement, PlacementEditor } from "./PlacementEditors.tsx";
import { isSetForProject, projectProvenance } from "./pages.ts";
import { projectRefusal } from "./refusals.ts";
import { describeReviewPeriod, REVIEW_DAYS_MAX, REVIEW_DAYS_MIN } from "./review-period.ts";
import { asDomains, DomainsEditor, describeDomains, describeMaxAge } from "./WebSearchEditors.tsx";

let reportedUnknown = false;

/** What the section reads of a project (the backoffice passes its governed project). */
export type BehaviourProject = Pick<ProjectDetail, "id" | "owner" | "ownerName">;

/**
 * Where settings are read and written: the project's own routes by default, governance routes
 * in the backoffice. `refresh` runs after every change.
 */
export interface SettingsBackend {
  settings: ProjectSettingResponse[] | undefined;
  error: Error | null;
  put: (key: string, value: unknown) => Promise<unknown>;
  reset: (key: string) => Promise<void>;
  refresh: () => Promise<unknown>;
}

/**
 * The settings this project may override, grouped, each with its effective value, where it comes
 * from and whether a level above locks it. Admins change or reset them; `narrowOnly` keys only
 * offer values at least as strict as the inherited one. A row the API does not describe as
 * overridable (an older server) is read-only.
 */
export function BehaviourSection({
  project,
  editable,
  backend,
  groups,
}: {
  project: BehaviourProject;
  editable: boolean;
  backend?: SettingsBackend;
  /** The blocks to show (a settings page's), in order; without it, every block under a lead. */
  groups?: readonly BehaviourDef["group"][];
}) {
  const own = useProjectSettingsBackend(project.id, backend === undefined);
  const { t } = useTranslation();
  const settings = { data: (backend ?? own).settings, error: (backend ?? own).error };
  if (settings.error) {
    return <Notice tone="danger">{settings.error.message}</Notice>;
  }
  if (!settings.data) {
    return <Skeleton className="h-48 w-full" />;
  }
  const scheme = schemeOf(settings.data);
  const rows = behaviourRows(settings.data);
  const efforts = settings.data.find((s) => s.key === "models.allowed")?.value;
  const allowedEfforts = Array.isArray(efforts) ? (efforts as string[]) : null;
  const unknown = settings.data.filter(
    (s) => s.overridable && s.overridable !== "no" && !BEHAVIOUR[s.key],
  );
  if (unknown.length > 0 && import.meta.env?.DEV && !reportedUnknown) {
    reportedUnknown = true;
    console.debug("Project settings not shown:", unknown.map((s) => s.key).join(", "));
  }
  return (
    <div data-esi-content="projects" className="flex flex-col gap-6">
      {groups ? null : (
        <p className="max-w-prose text-sm text-fg-secondary">{t("settings.behaviour.lead")}</p>
      )}
      {(groups ?? GROUPS).map((group) => {
        const inGroup = rowsOf(rows, group);
        return inGroup.length === 0 ? null : (
          <SettingsSectionBlock
            key={group}
            id={group}
            heading={t(`settings.behaviour.groups.${group}`)}
          >
            {inGroup.map((setting) => (
              <SettingRow
                key={setting.key}
                allowedEfforts={allowedEfforts}
                backend={backend ?? own}
                project={project}
                setting={setting}
                def={BEHAVIOUR[setting.key] as BehaviourDef}
                scheme={scheme}
                editable={
                  editable &&
                  !setting.locked &&
                  (setting.overridable === "yes" || setting.overridable === "narrowOnly")
                }
              />
            ))}
          </SettingsSectionBlock>
        );
      })}
      {(groups ?? GROUPS).includes("models") &&
      rowsOf(rows, "models").length > 0 &&
      project.owner.kind === "entity" ? (
        <ModelsLink entityId={project.owner.entityId} />
      ) : null}
    </div>
  );
}

/** The tenant's classification levels, as the settings list carries them. */
export const schemeOf = (settings: readonly ProjectSettingResponse[]) =>
  (settings.find((s) => s.key === "knowledge.classificationScheme")?.value as
    | ClassificationLevel[]
    | undefined) ?? [];

/**
 * The rows a project's settings show: overridable keys the app knows. A key with no effect on
 * this deployment (its tool is not deployed) stays backstage.
 */
export const behaviourRows = (settings: readonly ProjectSettingResponse[]) =>
  settings.filter((s) => s.overridable !== "no" && BEHAVIOUR[s.key] && !s.unavailable);

/** A block's rows, in the order `BEHAVIOUR` lists them: new keys extend it by data alone. */
export const rowsOf = (
  rows: readonly ProjectSettingResponse[],
  group: BehaviourDef["group"],
): ProjectSettingResponse[] =>
  Object.keys(BEHAVIOUR)
    .filter((key) => BEHAVIOUR[key]?.group === group)
    .flatMap((key) => rows.filter((r) => r.key === key));

/** A setting's label and help, as its row shows them. */
export function behaviourText(def: BehaviourDef) {
  return {
    label: translate(
      `settings.behaviour.keys.${def.copy}.label` as "settings.behaviour.keys.ocr.label",
    ),
    help: translate(
      `settings.behaviour.keys.${def.copy}.help` as "settings.behaviour.keys.ocr.help",
    ),
  };
}

/** The value in words. */
export function describe(
  def: BehaviourDef,
  value: unknown,
  scheme: readonly ClassificationLevel[],
) {
  switch (def.kind) {
    case "boolean":
      return value === true
        ? translate("settings.behaviour.on")
        : translate("settings.behaviour.off");
    case "residency":
      return translate(
        value === "global"
          ? "settings.behaviour.residency.global"
          : "settings.behaviour.residency.eu",
      );
    case "classification":
      return scheme.find((l) => l.id === value)?.label ?? String(value);
    case "pages":
      return translate("settings.behaviour.pages", { count: Number(value) });
    case "guidelines": {
      const g = (value ?? { pageTypes: [] }) as Guidelines;
      const types = g.pageTypes.map((p) => translate(`wiki.pageTypes.${p}`)).join(", ");
      const summary = translate("settings.behaviour.guidelines.summary", {
        language: g.language || translate("settings.behaviour.guidelines.sourcesLanguage"),
        types,
      });
      return g.structure
        ? `${summary} · ${translate("settings.behaviour.guidelines.withStructure")}`
        : summary;
    }
    case "placement":
      return describePlacement(value);
    case "reviewDays":
      return describeReviewPeriod(value);
    case "domains":
      return describeDomains(value);
    case "trust":
      return translate(
        `settings.behaviour.wikiTrust.${WIKI_TRUST_LEVELS.includes(value as WikiTrustLevel) ? (value as WikiTrustLevel) : "requester"}`,
      );
    case "approval":
      return translate(
        value === "always"
          ? "settings.behaviour.webFetchApproval.always"
          : "settings.behaviour.webFetchApproval.provenance",
      );
    case "days":
      return describeMaxAge(value);
    case "efforts": {
      const efforts = Array.isArray(value) ? (value as string[]) : [];
      const names = EFFORTS.filter((e) => efforts.includes(e)).map((e) =>
        translate(`adminSettings.modelRoles.${e}`),
      );
      return efforts.length === 1
        ? translate("settings.behaviour.models.locked", { effort: names[0] ?? "" })
        : names.join(", ");
    }
    case "effort":
      return EFFORTS.includes(value as (typeof EFFORTS)[number])
        ? translate(`adminSettings.modelRoles.${value as (typeof EFFORTS)[number]}`)
        : String(value);
    case "models":
      return modelNames(value).join(", ");
    case "egress":
      return describeEgress(value) ?? JSON.stringify(value);
  }
}

const EFFORTS = ["fast", "chat", "reasoning"] as const;

/** Catalogue display names of a stored model list; unknown keys are not shown. */
function modelNames(value: unknown): string[] {
  return (Array.isArray(value) ? (value as string[]) : []).flatMap((key) =>
    isModelKey(key) ? [MODEL_CATALOGUE[key].displayName] : [],
  );
}

/** The project's own settings routes. */
function useProjectSettingsBackend(projectId: string, enabled: boolean): SettingsBackend {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const settings = useProjectSettings(projectId, enabled);
  const headers = { [CSRF_HEADER]: csrfToken };
  return {
    settings: settings.data,
    error: settings.error,
    put: async (key, value) =>
      ok(
        await api.api.projects[":projectId"].settings[":key"].$put(
          { param: { projectId, key }, json: { value } },
          { headers },
        ),
      ),
    reset: async (key) => {
      const res = await api.api.projects[":projectId"].settings[":key"].$delete(
        { param: { projectId, key } },
        { headers },
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    // The settings list, what the files panel reads from it, and the header (residency).
    refresh: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: projectKeys.settings(projectId) }),
        queryClient.invalidateQueries({ queryKey: projectKeys.knowledge(projectId) }),
        queryClient.invalidateQueries({ queryKey: projectKeys.project(projectId), exact: true }),
      ]),
  };
}

/** The efforts, as stored (Fast, Balanced, Deep). */
const EFFORT_OPTIONS = [...EFFORTS];

/**
 * The input a project row edits, from its kind (openspec change settings-typed-controls, D1): the
 * shared control table picks the control; numbers carry the project's ceiling (D6).
 */
function inputOf(
  def: BehaviourDef,
  setting: ProjectSettingResponse,
  scheme: readonly ClassificationLevel[],
): SettingInput {
  const narrow = setting.overridable === "narrowOnly";
  const inherited = setting.inherited?.value ?? null;
  switch (def.kind) {
    case "boolean":
      return { type: "boolean" };
    case "residency":
      return { type: "select", options: ["eu", "global"] };
    case "classification":
      return { type: "select", options: scheme.map((l) => l.id) };
    case "trust":
      return { type: "select", options: [...WIKI_TRUST_LEVELS] };
    case "approval":
      return { type: "select", options: [...WEB_FETCH_APPROVALS] };
    case "effort":
      return { type: "select", options: EFFORT_OPTIONS };
    case "efforts":
      return { type: "multiselect", options: EFFORT_OPTIONS };
    case "pages":
      return {
        type: "number",
        min: 1,
        max: narrow ? pagesCeiling(inherited) : MAX_OCR_PAGES,
        nullable: false,
      };
    case "reviewDays":
      return { type: "number", min: REVIEW_DAYS_MIN, max: REVIEW_DAYS_MAX, nullable: true };
    case "days":
      return {
        type: "number",
        min: 1,
        max: narrow ? maxAgeCeiling(inherited) : MAX_AGE_DAYS,
        // No limit is never a narrowing (the server refuses it): Reset returns to inherited.
        nullable: !narrow,
      };
    case "domains":
      return { type: "domains", max: MAX_WEB_SEARCH_DOMAINS };
    case "guidelines":
    case "placement":
    case "models":
    case "egress":
      return { type: "json" };
  }
}

/**
 * Options a narrow-only row shows but does not offer (D6): those wider than the inherited value;
 * efforts outside what the project allows for its default effort.
 */
function disabledOptionsOf(
  def: BehaviourDef,
  setting: ProjectSettingResponse,
  input: SettingInput,
  allowedEfforts: readonly string[] | null,
): string[] | undefined {
  const options = input.type === "select" || input.type === "multiselect" ? input.options : [];
  if (def.kind === "effort") {
    return allowedEfforts ? options.filter((o) => !allowedEfforts.includes(o)) : undefined;
  }
  if (setting.overridable !== "narrowOnly") {
    return undefined;
  }
  // The registry-checked rule (`projectSettingLoosens`): an option wider than the inherited value.
  const inherited = setting.inherited?.value;
  return options.filter((o) =>
    projectSettingLoosens(setting.key, inherited, input.type === "multiselect" ? [o] : o),
  );
}

/**
 * A narrow-only `tools.egress` row's actions shown but not offered (D10): per audience, those
 * looser than the inherited action.
 */
function disabledEgressActionsOf(
  setting: ProjectSettingResponse,
): Partial<Record<EgressAudience, EgressAction[]>> | undefined {
  const inherited = egressPolicySchema.safeParse(setting.inherited?.value);
  if (setting.overridable !== "narrowOnly" || !inherited.success) {
    return undefined;
  }
  return Object.fromEntries(
    EGRESS_AUDIENCES.map((a) => [
      a,
      EGRESS_ACTIONS.filter((o) => EGRESS_RANK[o] < EGRESS_RANK[inherited.data[a]]),
    ]),
  );
}

/** The value a project row's confirmation names: where the inherited value comes from (D5). */
function inheritedSentence(
  setting: ProjectSettingResponse,
  project: BehaviourProject,
  value: string,
): string {
  const source = setting.inherited?.source ?? "instance";
  if (source === "entity") {
    const owner =
      project.owner.kind === "entity" &&
      setting.inherited.sourceEntityId?.toLowerCase() === project.owner.entityId.toLowerCase()
        ? project.ownerName
        : null;
    return owner
      ? translate("settingsControls.inheritedFrom.entity", { entity: owner, value })
      : translate("settingsControls.inheritedFrom.entityUnknown", { value });
  }
  return source === "tenant"
    ? translate("settingsControls.inheritedFrom.tenant", { value })
    : translate("settingsControls.inheritedFrom.instance", { value });
}

/** The project sentence for loosening a key (by its copy name; MCP groups share one). */
function projectLoosenSentence(def: BehaviourDef): string {
  const copy = def.copy.startsWith("mcpTools") ? "mcpTools" : def.copy;
  return translate(
    `settingsControls.projectLoosen.${copy}` as "settingsControls.projectLoosen.webFetch",
    {
      defaultValue: translate("settingsControls.loosenGeneric"),
    },
  );
}

/**
 * One project setting on the shared row (openspec change settings-typed-controls, slice B): the
 * control saves in place; a narrow-only row shows the wider options disabled (D6) and asks before
 * any move back toward the inherited value, Reset included (D5); locked, frozen and read-only rows
 * read in words (D4). The server stays the authority on narrowing.
 */
function SettingRow(props: {
  backend: SettingsBackend;
  project: BehaviourProject;
  setting: ProjectSettingResponse;
  def: BehaviourDef;
  scheme: readonly ClassificationLevel[];
  editable: boolean;
  /** The efforts the project allows (`models.allowed` in effect), for the default effort. */
  allowedEfforts: readonly string[] | null;
}) {
  const { backend, project, setting, def, scheme, editable } = props;
  const { t } = useTranslation();
  const key = setting.key;
  const narrow = setting.overridable === "narrowOnly";
  const inherited = setting.inherited?.value;
  const input = inputOf(def, setting, scheme);
  const { label, help } = behaviourText(def);
  const words = (value: unknown) => describe(def, value, scheme);
  // Mirrors the server's narrowing check, so a widening list is never sent.
  const widens = (draft: unknown) =>
    narrow && def.kind === "domains" && !webDomainsNarrow(asDomains(draft), asDomains(inherited));
  // `peer` with fewer than two who may publish: nobody could publish their own changes, so
  // choosing it asks first, with that warning (wiki-collaboration D9).
  const blocksPublishing = (to: unknown) =>
    def.kind === "trust" &&
    to === "peer" &&
    setting.publishers !== undefined &&
    setting.publishers < 2;
  return (
    <TypedSettingRow
      settingKey={key}
      input={input}
      label={label}
      help={help}
      helpExtra={<McpGroupTools settingKey={key} />}
      value={setting.value}
      locked={false}
      editable={editable}
      lockable={false}
      lockCopy="lock"
      resettable={editable && setting.source === "project"}
      resetText={t("settings.behaviour.reset")}
      setHere={isSetForProject(setting)}
      lockedAbove={setting.locked}
      marks={projectProvenance(setting, project)}
      unmarked={t("settings.behaviour.source.instance")}
      extra={editable && narrow ? <span>{t("settings.behaviour.narrowOnly")}</span> : null}
      describe={words}
      optionLabel={
        def.kind === "efforts" || def.kind === "effort"
          ? (o) => t(`adminSettings.modelRoles.${o as (typeof EFFORTS)[number]}`)
          : words
      }
      disabledOptions={disabledOptionsOf(def, setting, input, props.allowedEfforts)}
      disabledActions={def.kind === "egress" ? disabledEgressActionsOf(setting) : undefined}
      controlDisabled={
        narrow && def.kind === "boolean" && inherited === false && setting.value !== true
      }
      noOff={narrow && def.kind === "days"}
      loosens={(from, to) =>
        (narrow ? projectSettingLoosens(key, from, to) : settingLoosens(key, from, to)) ||
        (from !== to && blocksPublishing(to))
      }
      resetTo={{ value: inherited }}
      loosenSentence={(to) => {
        const few = blocksPublishing(to) ? t("settings.behaviour.wikiTrust.fewPublishers") : null;
        if (few && !projectSettingLoosens(key, setting.value, to)) {
          return few;
        }
        // A narrow-only value only ever moves back toward the inherited one.
        const effect =
          def.kind === "egress"
            ? (egressLoosenSentence(setting.value, to) ?? projectLoosenSentence(def))
            : projectLoosenSentence(def);
        // A project's own words either way; narrow-only rows add where the inherited value comes from.
        const loosening = narrow
          ? `${effect} ${inheritedSentence(setting, project, words(inherited))}`
          : effect;
        return few && loosening ? `${loosening} ${few}` : loosening;
      }}
      guardUnsaved
      run={(action) =>
        action.type === "put" ? backend.put(key, action.value) : backend.reset(key)
      }
      settle={backend.refresh}
      refusal={projectRefusal}
      canSave={(draft) => !widens(draft)}
      editor={({ id, value, onChange }) => (
        <ProjectEditor
          id={id}
          projectId={project.id}
          def={def}
          setting={setting}
          value={value}
          widens={widens(value)}
          onChange={onChange}
        />
      )}
      below={
        // `peer` with fewer than two who may publish: nobody could publish their own changes.
        editable && blocksPublishing(setting.value) ? (
          <Notice tone="warning">{t("settings.behaviour.wikiTrust.fewPublishers")}</Notice>
        ) : null
      }
    />
  );
}

/** A project's explicit editors (lists and structured values), inside the row's disclosure. */
function ProjectEditor(props: {
  id: string;
  projectId: string;
  def: BehaviourDef;
  setting: ProjectSettingResponse;
  value: unknown;
  widens: boolean;
  onChange: (value: unknown) => void;
}) {
  const { id, def, setting, value, onChange } = props;
  const { t } = useTranslation();
  switch (def.kind) {
    case "guidelines":
      return <GuidelinesEditor id={id} value={value as Guidelines} onChange={onChange} />;
    case "placement":
      return (
        <PlacementEditor id={id} projectId={props.projectId} value={value} onChange={onChange} />
      );
    case "domains":
      return (
        <div className="flex flex-col gap-3">
          {setting.overridable === "narrowOnly" ? (
            <p className="text-xs text-fg-muted">
              {t("settings.behaviour.web.inherited", {
                value: describeDomains(setting.inherited?.value),
              })}
            </p>
          ) : null}
          <DomainsEditor id={id} value={value} onChange={onChange} />
          {props.widens ? (
            <Notice tone="warning">{t("settings.behaviour.web.widens")}</Notice>
          ) : null}
        </div>
      );
    case "models": {
      // Narrow-only: the options are what the project inherits (at least one stays).
      const inherited = Array.isArray(setting.inherited?.value)
        ? (setting.inherited.value as string[])
        : [];
      const options = inherited.flatMap((key) =>
        isModelKey(key) ? [{ value: key, label: MODEL_CATALOGUE[key].displayName }] : [],
      );
      const picked = Array.isArray(value) ? (value as string[]) : [];
      return (
        <fieldset aria-labelledby={`${id}-label`} className="flex flex-wrap gap-x-5 gap-y-2">
          {options.map((o) => {
            const on = picked.includes(o.value);
            return (
              <div key={o.value} className="flex items-center gap-2">
                <Checkbox
                  id={`${id}-${o.value}`}
                  checked={on}
                  disabled={on && picked.length === 1}
                  onCheckedChange={(next) =>
                    onChange(
                      next === true
                        ? options
                            .map((x) => x.value)
                            .filter((x) => x === o.value || picked.includes(x))
                        : picked.filter((x) => x !== o.value),
                    )
                  }
                />
                <Label htmlFor={`${id}-${o.value}`} className="font-normal">
                  {o.label}
                </Label>
              </div>
            );
          })}
        </fieldset>
      );
    }
    default:
      return null;
  }
}

export function GuidelinesEditor(props: {
  id: string;
  value: Guidelines;
  onChange: (value: Guidelines) => void;
}) {
  const { id, value, onChange } = props;
  const { t } = useTranslation();
  const set = (patch: Partial<Guidelines>) => {
    const next = { ...value, ...patch };
    // Empty texts mean "not set": the server's schema refuses empty strings.
    if (!next.language?.trim()) {
      delete next.language;
    }
    if (!next.structure?.trim()) {
      delete next.structure;
    }
    onChange(next);
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-language`}>{t("settings.behaviour.guidelines.language")}</Label>
        <Input
          id={`${id}-language`}
          maxLength={60}
          value={value.language ?? ""}
          aria-describedby={`${id}-language-hint`}
          className="sm:w-72"
          onChange={(e) => set({ language: e.currentTarget.value })}
        />
        <p id={`${id}-language-hint`} className="text-xs text-fg-muted">
          {t("settings.behaviour.guidelines.languageHint")}
        </p>
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium text-fg">
          {t("settings.behaviour.guidelines.pageTypes")}
        </legend>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {PAGE_TYPES.map((type) => {
            const on = value.pageTypes.includes(type);
            return (
              <div key={type} className="flex items-center gap-2">
                <Checkbox
                  id={`${id}-type-${type}`}
                  checked={on}
                  // At least one page type stays.
                  disabled={on && value.pageTypes.length === 1}
                  onCheckedChange={(next) =>
                    set({
                      pageTypes:
                        next === true
                          ? PAGE_TYPES.filter((p) => p === type || value.pageTypes.includes(p))
                          : value.pageTypes.filter((p) => p !== type),
                    })
                  }
                />
                <Label htmlFor={`${id}-type-${type}`} className="font-normal">
                  {t(`wiki.pageTypes.${type}`)}
                </Label>
              </div>
            );
          })}
        </div>
      </fieldset>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-structure`}>{t("settings.behaviour.guidelines.structure")}</Label>
        <Textarea
          id={`${id}-structure`}
          maxLength={2000}
          value={value.structure ?? ""}
          aria-describedby={`${id}-structure-hint`}
          className="min-h-20"
          onChange={(e) => set({ structure: e.currentTarget.value })}
        />
        <p id={`${id}-structure-hint`} className="text-xs text-fg-muted">
          {t("settings.behaviour.guidelines.structureHint")}
        </p>
      </div>
    </div>
  );
}

/**
 * For the organisation's admins, the way to the rest of the project's model settings (model-
 * picking 5.11): labs, families, pins and health are set on the backoffice Models screen at the
 * project's owning entity, inherited by the project.
 */
function ModelsLink({ entityId }: { entityId: string }) {
  const { t } = useTranslation();
  const { roles } = useSession();
  if (!roles.includes("tenant_admin") && !roles.includes("entity_admin")) {
    return null;
  }
  return (
    <p className="text-xs">
      <Link
        href={`${MODELS_PATH}?level=${encodeURIComponent(entityId)}`}
        className="text-accent-text underline-offset-2 hover:underline"
      >
        {t("settings.behaviour.modelsLink")}
      </Link>
    </p>
  );
}
